/**
 * Whether a paid anonymization action can be offered, and in what shape.
 *
 * Three states rather than a boolean, because "we don't know yet" must not
 * look like "you can't": showing a padlock to a paying customer while their
 * entitlement is still in flight is worse than showing nothing at all.
 */
export type AccessState =
  /** Render the action normally. */
  | "available"
  /** Render it with the padlock; the click opens the upsell. */
  | "locked"
  /** Render it without a padlock and without an effect — we don't know yet. */
  | "inert"
  /** Don't render it at all: the environment has the feature switched off. */
  | "absent";

export type AccessInput = {
  /** The build-time env kill switch for this feature. */
  featureEnabled: boolean;
  /** Whether billing is switched on in this environment at all. */
  billingEnabled: boolean;
  /** The entitlement, or `undefined` while it is still loading. */
  entitled: boolean | undefined;
};

/**
 * Resolves the four inputs into one observable state.
 *
 * The precedence is deliberate and load-bearing:
 *
 * 1. **env off ⇒ absent.** A padlock on a feature the environment cannot turn
 *    on would sell something that does not exist here — paying would not
 *    unlock it.
 * 2. **billing off ⇒ available.** Same escape hatch every other paid feature
 *    honors: with no billing surface there is no way to buy, so gating would
 *    strand the feature with no path out.
 * 3. **entitlement unknown ⇒ inert.** See above: silence beats a wrong lock.
 * 4. Otherwise the plan decides.
 */
export const resolveAccessState = (input: AccessInput): AccessState => {
  if (!input.featureEnabled) return "absent";
  if (!input.billingEnabled) return "available";
  if (input.entitled === undefined) return "inert";
  return input.entitled ? "available" : "locked";
};
